import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { salesCyclesApi } from '../services/api';
import SalesStagesScoring from '../components/SalesStagesScoring';
import { AlertTriangle } from 'lucide-react';

const Dashboard: React.FC = () => {
  const [selectedCycleTypes, setSelectedCycleTypes] = useState<string[]>(() => {
    // Initialize from localStorage if available, otherwise default to ["NEWBUSINESS"]
    const saved = localStorage.getItem('dashboard-cycle-type-filter');
    if (!saved) return ['NEWBUSINESS'];

    try {
      // Try to parse as JSON array (new format)
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : ['NEWBUSINESS'];
    } catch {
      // If parsing fails, it's likely the old single-string format
      // Convert single string to array, then clear old format
      return saved ? [saved] : ['NEWBUSINESS'];
    }
  });

  const [selectedAccountValues, setSelectedAccountValues] = useState<string[]>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('dashboard-account-value-filter');
    if (!saved) return [];
    
    try {
      // Try to parse as JSON array (new format)
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      // If parsing fails, it's likely the old single-string format
      // Convert single string to array, then clear old format
      localStorage.removeItem('dashboard-account-value-filter');
      return saved ? [saved] : [];
    }
  });

  const [selectedSalesRep, setSelectedSalesRep] = useState<string | null>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('dashboard-sales-rep-filter');
    return saved || null;
  });

  const [selectedTeam, setSelectedTeam] = useState<string | null>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('dashboard-team-filter');
    return saved || null;
  });

  const [selectedMeetingDispositions, setSelectedMeetingDispositions] = useState<string[]>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('dashboard-meeting-disposition-filter');
    if (!saved) {
      // Default: all dispositions except BAD_FIT and TRASH, including NOT_ANALYZED (Conflict removed)
      return ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
    }

    try {
      const parsed = JSON.parse(saved);
      // Filter out CONFLICT from any saved values (legacy cleanup)
      const filtered = Array.isArray(parsed) ? parsed.filter((v: string) => v !== 'CONFLICT') : ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
      return filtered.length > 0 ? filtered : ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
    } catch {
      return ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
    }
  });

  // Parse meeting dispositions to convert ENTERPRISE_DEAL/ENTERPRISE_ZERO to base dispositions
  // This is needed because the backend expects base values like 'ENTERPRISE', 'TRANSACTIONAL'
  const parseDispositionsForAPI = (dispositions?: string[]): string[] => {
    if (!dispositions || dispositions.length === 0) {
      return [];
    }

    const parsedDispositions: string[] = [];

    for (const d of dispositions) {
      if (d === 'TRANSACTIONAL_DEAL' || d === 'TRANSACTIONAL_ZERO') {
        parsedDispositions.push('TRANSACTIONAL');
      } else if (d === 'ENTERPRISE_DEAL' || d === 'ENTERPRISE_ZERO') {
        parsedDispositions.push('ENTERPRISE');
      } else {
        // Regular dispositions: TRANSACTIONAL, ENTERPRISE, NURTURE, NOT_ANALYZED, BAD_FIT, TRASH
        parsedDispositions.push(d);
      }
    }

    // Remove duplicates
    return Array.from(new Set(parsedDispositions));
  };

  // Get parsed meeting dispositions for API calls
  const parsedMeetingDispositions = parseDispositionsForAPI(selectedMeetingDispositions);

  const { data: stagesScoringData, isLoading: stagesScoringLoading, error: stagesScoringError, isFetching } = useQuery(
    ['deals-stages-scoring', selectedCycleTypes?.join(',') || 'all', selectedAccountValues?.join(',') || 'all', selectedSalesRep || 'all', selectedTeam || 'all', parsedMeetingDispositions?.join(',') || 'default'],
    () => salesCyclesApi.getStagesScoring(
      selectedCycleTypes?.length ? selectedCycleTypes.join(',') : undefined,
      selectedAccountValues?.length ? selectedAccountValues.join(',') : undefined,
      selectedSalesRep || undefined,
      selectedTeam || undefined,
      parsedMeetingDispositions?.length ? parsedMeetingDispositions.join(',') : undefined
    ),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes for fresher data
      cacheTime: 5 * 60 * 1000, // 5 minutes cache
      refetchOnWindowFocus: false, // Don't refetch when switching back to tab
      retry: 1, // Fewer retries for faster failure response
      keepPreviousData: true, // Keep old data while fetching new data
    }
  );

  const handleCycleTypesChange = (cycleTypes: string[]) => {
    setSelectedCycleTypes(cycleTypes);
    // Save to localStorage
    if (cycleTypes.length > 0) {
      localStorage.setItem('dashboard-cycle-type-filter', JSON.stringify(cycleTypes));
    } else {
      localStorage.removeItem('dashboard-cycle-type-filter');
    }
  };

  const handleAccountValuesChange = (accountValues: string[]) => {
    setSelectedAccountValues(accountValues);
    // Save to localStorage
    if (accountValues.length > 0) {
      localStorage.setItem('dashboard-account-value-filter', JSON.stringify(accountValues));
    } else {
      localStorage.removeItem('dashboard-account-value-filter');
    }
  };

  const handleSalesRepChange = (salesRep: string | null) => {
    setSelectedSalesRep(salesRep);
    // Save to localStorage
    if (salesRep) {
      localStorage.setItem('dashboard-sales-rep-filter', salesRep);
    } else {
      localStorage.removeItem('dashboard-sales-rep-filter');
    }
  };

  const handleTeamChange = (team: string | null) => {
    setSelectedTeam(team);
    // Clear the selected sales rep when team changes since reps will be filtered by team
    setSelectedSalesRep(null);

    // Save to localStorage
    if (team) {
      localStorage.setItem('dashboard-team-filter', team);
    } else {
      localStorage.removeItem('dashboard-team-filter');
    }

    // Clear sales rep from localStorage since it's no longer valid
    localStorage.removeItem('dashboard-sales-rep-filter');
  };

  const handleMeetingDispositionsChange = (dispositions: string[]) => {
    setSelectedMeetingDispositions(dispositions);
    // Save to localStorage
    if (dispositions.length > 0) {
      localStorage.setItem('dashboard-meeting-disposition-filter', JSON.stringify(dispositions));
    } else {
      localStorage.removeItem('dashboard-meeting-disposition-filter');
    }
  };

  // Clear localStorage on component unmount (optional)
  useEffect(() => {
    return () => {
      // Keep the filter in localStorage for persistence
    };
  }, []);

  if (stagesScoringLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        <div className="text-gray-600 dark:text-gray-400 text-sm font-inter">
          Loading sales stages data...
        </div>
      </div>
    );
  }

  if (stagesScoringError) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 font-inter">
              Error loading sales stages data
            </h3>
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 font-inter">
              Please check your connection and try again.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Sales Stages and Scoring</h1>
        <p className="text-gray-600 dark:text-gray-400 text-sm font-inter mt-1">
          Score reflects the completion of exit criteria for each stage
        </p>
      </div>

      {/* Sales Stages and Scoring */}
      <SalesStagesScoring
        stages={stagesScoringData || []}
        isLoading={stagesScoringLoading && !stagesScoringData} // Only show loading if no data exists
        isFetching={isFetching} // Show subtle loading for filter changes
        selectedCycleTypes={selectedCycleTypes}
        onCycleTypesChange={handleCycleTypesChange}
        selectedAccountValues={selectedAccountValues}
        onAccountValuesChange={handleAccountValuesChange}
        selectedMeetingDispositions={selectedMeetingDispositions}
        onMeetingDispositionsChange={handleMeetingDispositionsChange}
        selectedSalesRep={selectedSalesRep}
        onSalesRepChange={handleSalesRepChange}
        selectedTeam={selectedTeam}
        onTeamChange={handleTeamChange}
        hideHeader={true} // Add prop to hide the header in component
        defaultFiltersCollapsed={false} // Show filters expanded by default on dashboard
      />
    </div>
  );
};

export default Dashboard; 